import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  Container,
  Divider,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  FileDownload as FileDownloadIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  CurrencyRupee as CurrencyRupeeIcon,
} from '@mui/icons-material';
import Layout from '../../components/layout/Layout';
import ReportMetricCard from '../../components/reports/ReportMetricCard';
import { reportPageSx } from '../../components/reports/reportPageLayout';
import locationService from '../../services/locationService';
import axiosInstance from '../../utils/axios';
import API_CONFIG from '../../config/api.config';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { formatDate as formatDateRegional } from '../../utils/dateFormatter';
import * as XLSX from 'xlsx';

const DayCloseReport = () => {
  
  // State for filters
  const [filters, setFilters] = useState({
    locationId: '',
    startDate: (() => {
      const date = new Date();
      date.setDate(date.getDate() - 30);
      return date.toISOString().split('T')[0];
    })(),
    endDate: new Date().toISOString().split('T')[0],
  });

  // State for locations
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [locationsLoading, setLocationsLoading] = useState(false);

  // State for summary data
  const [summaryData, setSummaryData] = useState({
    totalReports: 0,
    totalClosedDays: 0,
    totalOpenDays: 0,
    totalSalesSum: 0,
    totalCashSalesSum: 0,
    totalCardSalesSum: 0,
    totalUpiSalesSum: 0,
    totalCreditSalesSum: 0,
    totalOrdersSum: 0,
    totalProductsSoldSum: 0,
    averageDailySales: 0,
  });

  // State for day close reports
  const [dayCloseReports, setDayCloseReports] = useState([]);

  // Add pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Add export state
  const [isExporting, setIsExporting] = useState(false);

  // State for details dialog
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [detailProducts, setDetailProducts] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTotals, setDetailTotals] = useState(null); // optional override from /day-close/summary

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const parseReportDate = (value) => {
    if (!value) return null;
    if (Array.isArray(value)) {
      const [year, month, day, hour = 0, minute = 0, second = 0] = value;
      return new Date(year, (month || 1) - 1, day || 1, hour, minute, second || 0);
    }
    if (typeof value === 'number') {
      return new Date(value);
    }
    if (typeof value === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return new Date(`${value}T00:00:00`);
      }
      return new Date(value.includes(' ') && !value.includes('T') ? value.replace(' ', 'T') : value);
    }
    if (value instanceof Date) return value;
    if (typeof value === 'object') {
      const year = value.year ?? value.y;
      const month = value.month ?? value.m;
      const day = value.day ?? value.d;
      if (year && month && day) {
        return new Date(year, month - 1, day);
      }
    }
    return null;
  };

  const toIsoDate = (value) => {
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    const date = parseReportDate(value) || new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Format date using regional settings
  const formatDate = (date) => {
    const parsedDate = parseReportDate(date);
    if (!parsedDate || isNaN(parsedDate.getTime())) return '-';
    return formatDateRegional(parsedDate);
  };

  // Fetch locations on component mount
  useEffect(() => {
    fetchLocations();
  }, []);

  // Function to fetch locations
  const fetchLocations = async () => {
    try {
      setLocationsLoading(true);
      const response = await locationService.list({ page: 0, size: 1000 });
      setLocations(response.content || []);
    } catch (err) {
      console.error('Error fetching locations:', err);
      toast.error('Failed to load locations');
    } finally {
      setLocationsLoading(false);
    }
  };

  // Get status chip color and label
  const getStatusChip = (isDayClosed) => {
    if (isDayClosed) {
      return { color: 'success', label: 'Closed', icon: <CheckCircleIcon /> };
    } else {
      return { color: 'warning', label: 'Open', icon: <AccessTimeIcon /> };
    }
  };

  // Function to handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleGenerateReport = async () => {
    try {
      setLoading(true);
      
      // Build query parameters
      const params = {
        startDate: filters.startDate,
        endDate: filters.endDate,
      };
      
      if (filters.locationId) {
        params.locationId = filters.locationId;
      }

      // Fetch day close report from API
      console.log('Fetching day close report with params:', params);
      const response = await axiosInstance.get(API_CONFIG.ENDPOINTS.DAY_CLOSE_REPORT, { params });
      console.log('Day close report response:', response.data);
      
      // Update summary data
      setSummaryData(response.data.summary || {
        totalReports: 0,
        totalClosedDays: 0,
        totalOpenDays: 0,
        totalSalesSum: 0,
        totalCashSalesSum: 0,
        totalCardSalesSum: 0,
        totalUpiSalesSum: 0,
        totalCreditSalesSum: 0,
        totalOrdersSum: 0,
        totalProductsSoldSum: 0,
        averageDailySales: 0,
      });
      
      // Update day close reports
      setDayCloseReports(response.data.dayCloseReports || []);
      
      // Reset to first page when new data is loaded
      setPage(0);
      
      toast.success('Day close report generated successfully');
    } catch (err) {
      console.error('Error generating day close report:', err);
      console.error('Error details:', err.response?.data || err.message);
      
      let errorMessage = 'Failed to generate day close report';
      if (err.response?.status === 404) {
        errorMessage = 'Day close report endpoint not found. Please check the backend configuration.';
      } else if (err.response?.status === 403) {
        errorMessage = 'You do not have permission to access day close reports.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Function to handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Function to handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Function to handle view details
  const handleViewDetails = (report) => {
    console.log('[DayCloseDetails] Opening dialog with report data:', report);
    console.log('[DayCloseDetails] isDayClosed:', report.isDayClosed);
    console.log('[DayCloseDetails] createdAt:', report.createdAt);
    console.log('[DayCloseDetails] updatedAt:', report.updatedAt);
    setSelectedReport(report);
    setDetailsDialogOpen(true);
  };

  // Function to close details dialog
  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedReport(null);
    setDetailProducts([]);
    setDetailTotals(null);
  };

  const handleDownloadPDF = async () => {
    if (!selectedReport) return;
    
    try {
      console.log('[DayClosePDF] Downloading PDF for report:', selectedReport);
      
      const iso = toIsoDate(selectedReport.reportDate);
      
      const params = { 
        locationId: selectedReport.locationId, 
        date: iso 
      };
      
      console.log('[DayClosePDF] Requesting PDF with params:', params);
      
      const response = await axiosInstance.get(`${API_CONFIG.ENDPOINTS.REPORTS}/day-close/pdf`, {
        params,
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      });

      // Create a blob from the PDF stream
      const blob = new Blob([response.data], { type: 'application/pdf' });
      
      // Create a link element
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `DayCloseReport_${selectedReport.locationName || 'All'}_${iso}.pdf`;
      
      // Append to body, click and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the URL object
      window.URL.revokeObjectURL(link.href);
      
      console.log('[DayClosePDF] PDF downloaded successfully');
    } catch (error) {
      console.error('[DayClosePDF] Error downloading PDF:', error);
      // You can add a toast notification here if needed
    }
  };

  const handleExportToExcel = async () => {
    try {
      setIsExporting(true);
      
      // Prepare export data
      const exportData = dayCloseReports.map((report, idx) => ({
        'S.No': idx + 1,
        'Date': formatDate(report.reportDate),
        'Location': report.locationName,
        'User': report.userName,
        'Opening Cash': report.openingCash,
        'Total Sales': report.totalSales,
        'Cash Sales': report.cashSales,
        'Card Sales': report.cardSales,
        'UPI Sales': report.upiSales,
        'Credit Sales': report.creditSales,
        'Total Orders': report.totalOrders,
        'Products Sold': report.totalProductsSold,
        'Status': report.isDayClosed ? 'Closed' : 'Open',
      }));
      
      const ws = XLSX.utils.json_to_sheet(exportData);
      ws['!cols'] = [
        { wch: 5 }, { wch: 15 }, { wch: 20 }, { wch: 20 }, { wch: 15 }, 
        { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
        { wch: 12 }, { wch: 15 }, { wch: 10 }
      ];
      
      const wb = XLSX.utils.book_new();
      
      // Add summary information at the top
      const summaryRows = [
        ['Day Close Report by Location'],
        ['Generated on: ' + new Date().toLocaleString()],
        ['Date Range: ' + formatDate(filters.startDate) + ' to ' + formatDate(filters.endDate)],
        [''],
        ['Summary:'],
        ['Total Reports', summaryData.totalReports],
        ['Closed Days', summaryData.totalClosedDays],
        ['Open Days', summaryData.totalOpenDays],
        ['Total Sales', summaryData.totalSalesSum],
        ['Cash Sales', summaryData.totalCashSalesSum],
        ['Card Sales', summaryData.totalCardSalesSum],
        ['UPI Sales', summaryData.totalUpiSalesSum],
        ['Credit Sales', summaryData.totalCreditSalesSum],
        ['Total Orders', summaryData.totalOrdersSum],
        ['Products Sold', summaryData.totalProductsSoldSum],
        ['Average Daily Sales', summaryData.averageDailySales],
        ['']
      ];
      
      XLSX.utils.sheet_add_aoa(ws, summaryRows, { origin: 'A1' });
      XLSX.utils.book_append_sheet(wb, ws, 'Day Close Report');
      
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `day_close_report_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Day close report exported successfully');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export day close report');
    } finally {
      setIsExporting(false);
    }
  };

  // Helpers to format date/time values that may be array or string
  const formatTime = (value) => {
    try {
      if (!value) return '-';
      let d;
      if (Array.isArray(value)) {
        const [y, m, day, h = 0, min = 0, s = 0] = value;
        d = new Date(y, (m || 1) - 1, day || 1, h, min, s);
      } else {
        d = new Date(value);
      }
      if (isNaN(d.getTime())) return '-';
      return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    } catch {
      return '-';
    }
  };

  const sameDate = (dateA, dateB) => {
    try {
      const a = Array.isArray(dateA) ? new Date(dateA[0], dateA[1]-1, dateA[2]) : new Date(dateA);
      const b = Array.isArray(dateB) ? new Date(dateB[0], dateB[1]-1, dateB[2]) : new Date(dateB);
      return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    } catch {
      return false;
    }
  };

  // Load product details (and latest totals) when opening dialog
  useEffect(() => {
    const loadDetails = async () => {
      if (!detailsDialogOpen || !selectedReport) return;
      setDetailLoading(true);
      try {
        // Fetch product details for this report's date/location via reports endpoint (no Day Close permission needed)
        // Convert to ISO yyyy-MM-dd - handle both array and string formats
        const iso = toIsoDate(selectedReport.reportDate);
        
        console.log('[DayCloseDetails] Original reportDate:', selectedReport.reportDate);
        console.log('[DayCloseDetails] Converted ISO date:', iso);

        const params = { locationId: selectedReport.locationId, date: iso };
        console.log('[DayCloseDetails] Requesting product details with params:', params);
        const productsRes = await axiosInstance.get(`${API_CONFIG.ENDPOINTS.REPORTS}/day-close/products`, {
          params
        });
        console.log('[DayCloseDetails] Products response count:', Array.isArray(productsRes?.data) ? productsRes.data.length : 'N/A');
        setDetailProducts(productsRes?.data || []);
        // Keep totals from the selected row (they're already correct for that date)
        setDetailTotals(null);
      } catch (e) {
        setDetailProducts([]);
        setDetailTotals(null);
      } finally {
        setDetailLoading(false);
      }
    };
    loadDetails();
  }, [detailsDialogOpen, selectedReport]);

  const getRoundedProductAmount = (amount) => Math.round(Number(amount || 0));

  const getRoundedProductsTotal = () =>
    (detailProducts || []).reduce((sum, product) => sum + getRoundedProductAmount(product.totalAmount), 0);

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ ...reportPageSx.container, fontSize: '0.92rem' }}>
        <Box sx={{ py: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
            <Typography variant="h4" sx={{ ...reportPageSx.pageTitle, display: 'flex', alignItems: 'center', gap: 1, m: 0 }}>
              <AssessmentIcon color="primary" />
              Day Close Report by Location
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={handleExportToExcel}
              disabled={isExporting || dayCloseReports.length === 0}
              startIcon={isExporting ? <CircularProgress size={18} color="inherit" /> : <FileDownloadIcon />}
            >
              {isExporting ? 'Exporting...' : 'Export to Excel'}
            </Button>
          </Box>

          <Card sx={reportPageSx.filterCard}>
            <CardContent sx={reportPageSx.filterCardContent}>
              <Grid
                container
                spacing={reportPageSx.filterGrid.spacing}
                alignItems="flex-end"
                sx={{ flexWrap: { xs: 'wrap', lg: 'nowrap' } }}
              >
                <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="day-close-location-label">Location</InputLabel>
                    <Select
                      labelId="day-close-location-label"
                      label="Location"
                      value={filters.locationId}
                      onChange={(e) => handleFilterChange('locationId', e.target.value)}
                      disabled={locationsLoading}
                    >
                      <MenuItem value="">All Locations</MenuItem>
                      {locationsLoading ? (
                        <MenuItem disabled>
                          <Box display="flex" alignItems="center" gap={1}>
                            <CircularProgress size={18} />
                            <span>Loading...</span>
                          </Box>
                        </MenuItem>
                      ) : (
                        locations.map((location) => (
                          <MenuItem key={location.id} value={location.id}>
                            {location.name}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Start Date"
                    size="small"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0 }}>
                  <TextField
                    fullWidth
                    type="date"
                    label="End Date"
                    size="small"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} sx={{ minWidth: 0, flexShrink: 0 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleGenerateReport}
                    disabled={loading}
                    sx={{ ...reportPageSx.filterButton, whiteSpace: 'nowrap' }}
                  >
                    {loading ? <CircularProgress size={20} color="inherit" /> : 'Generate Report'}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={4} md={2}>
              <ReportMetricCard
                label="Total Reports"
                value={summaryData.totalReports}
                icon={<AssessmentIcon />}
                accentColor="primary.main"
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <ReportMetricCard
                label="Closed Days"
                value={summaryData.totalClosedDays}
                icon={<CheckCircleIcon />}
                accentColor="success.main"
                valueColor="success.main"
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <ReportMetricCard
                label="Open Days"
                value={summaryData.totalOpenDays}
                icon={<AccessTimeIcon />}
                accentColor="warning.main"
                valueColor="warning.main"
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <ReportMetricCard
                label="Total Sales"
                value={formatCurrency(summaryData.totalSalesSum)}
                icon={<CurrencyRupeeIcon />}
                accentColor="info.main"
                valueColor="info.main"
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <ReportMetricCard
                label="Credit Sales"
                value={formatCurrency(summaryData.totalCreditSalesSum)}
                icon={<CurrencyRupeeIcon />}
                accentColor="warning.main"
                valueColor="warning.main"
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <ReportMetricCard
                label="Avg Daily Sales"
                value={formatCurrency(summaryData.averageDailySales)}
                icon={<CurrencyRupeeIcon />}
                accentColor="secondary.main"
              />
            </Grid>
          </Grid>

          <Card sx={reportPageSx.sectionCard}>
            <CardContent sx={{ ...reportPageSx.sectionCardContent, p: 0, '&:last-child': { pb: 0 } }}>
          <TableContainer sx={{ fontSize: '0.92rem' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell align="right">Opening Cash</TableCell>
                  <TableCell align="right">Total Sales</TableCell>
                  <TableCell align="right">Cash</TableCell>
                  <TableCell align="right">Card</TableCell>
                  <TableCell align="right">UPI</TableCell>
                  <TableCell align="right">Credit</TableCell>
                  <TableCell align="right">Orders</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center">
                      <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : dayCloseReports.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center">
                      <Typography variant="body1" color="text.secondary">
                        No day close reports found. Click "Generate Report" to load data.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  // Apply pagination to day close reports - one row per date
                  dayCloseReports
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((report, index) => {
                      const status = getStatusChip(report.isDayClosed);
                      return (
                        <TableRow key={`${report.reportDate}-${report.locationId}-${index}`}>
                          <TableCell>{formatDate(report.reportDate)}</TableCell>
                          <TableCell>{report.locationName}</TableCell>
                          <TableCell>{report.userName}</TableCell>
                          <TableCell align="right">{formatCurrency(report.openingCash)}</TableCell>
                          <TableCell align="right">{formatCurrency(report.totalSales)}</TableCell>
                          <TableCell align="right">{formatCurrency(report.cashSales)}</TableCell>
                          <TableCell align="right">{formatCurrency(report.cardSales)}</TableCell>
                          <TableCell align="right">{formatCurrency(report.upiSales)}</TableCell>
                          <TableCell align="right">{formatCurrency(report.creditSales)}</TableCell>
                          <TableCell align="right">{report.totalOrders}</TableCell>
                          <TableCell>
                            <Chip
                              label={status.label}
                              color={status.color}
                              size="small"
                              icon={status.icon}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleViewDetails(report)}
                              startIcon={<AssessmentIcon />}
                            >
                              View Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                )}
              </TableBody>
            </Table>
            {!loading && dayCloseReports.length > 0 && (
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={dayCloseReports.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                size="small"
              />
            )}
          </TableContainer>
            </CardContent>
          </Card>
        </Box>
      </Container>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <AssessmentIcon color="primary" />
            <Typography variant="h6">Day Close Report Details</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedReport && (
            <Box>
              {/* Header Information - Compact */}
              <Card sx={{ mb: 2, p: 1.5, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                      {formatDate(selectedReport.reportDate)}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                      {selectedReport.locationName} • {selectedReport.userName}
                      {selectedReport.totalSessions > 1 && ` • ${selectedReport.totalSessions} Sessions`}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                      <strong>Day Begin:</strong> {formatTime(selectedReport.createdAt)}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                      <strong>Day Close:</strong> {selectedReport.isDayClosed ? formatTime(selectedReport.updatedAt) : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box display="flex" justifyContent="flex-end" alignItems="center">
                      <Chip
                        label={selectedReport.isDayClosed ? 'Closed' : 'Open'}
                        color={selectedReport.isDayClosed ? 'success' : 'warning'}
                        icon={selectedReport.isDayClosed ? <CheckCircleIcon /> : <AccessTimeIcon />}
                        size="small"
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Card>

              {/* Sales Summary - Compact */}
              <Card sx={{ mb: 2, p: 1.5 }}>
                <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem' }}>Sales Summary</Typography>
                <Grid container spacing={1.5} alignItems="stretch">
                  {/* Opening Cash */}
                  <Grid item xs={6} sm={2.4}>
                    <Box textAlign="center" p={1.5} bgcolor="grey.50" borderRadius={1}>
                      <Typography variant="h6" color="primary" sx={{ fontSize: '1rem' }}>
                        {formatCurrency((detailTotals?.openingCash ?? selectedReport.openingCash))}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        Opening Cash
                      </Typography>
                    </Box>
                  </Grid>
                  {/* Cash Sales */}
                  <Grid item xs={6} sm={2.4}>
                    <Box textAlign="center" p={1.5} bgcolor="grey.50" borderRadius={1}>
                      <Typography variant="h6" color="info.main" sx={{ fontSize: '1rem' }}>
                        {formatCurrency((detailTotals?.cashSales ?? selectedReport.cashSales))}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        Cash Sales
                      </Typography>
                    </Box>
                  </Grid>
                  {/* Card Sales */}
                  <Grid item xs={6} sm={2.4}>
                    <Box textAlign="center" p={1.5} bgcolor="grey.50" borderRadius={1}>
                      <Typography variant="h6" color="secondary.main" sx={{ fontSize: '1rem' }}>
                        {formatCurrency((detailTotals?.cardSales ?? selectedReport.cardSales))}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        Card Sales
                      </Typography>
                    </Box>
                  </Grid>
                  {/* UPI Sales */}
                  <Grid item xs={6} sm={2.4}>
                    <Box textAlign="center" p={1.5} bgcolor="grey.50" borderRadius={1}>
                      <Typography variant="h6" color="success.main" sx={{ fontSize: '1rem' }}>
                        {formatCurrency((detailTotals?.upiSales ?? selectedReport.upiSales))}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        UPI Sales
                      </Typography>
                    </Box>
                  </Grid>
                  {/* Credit Sales */}
                  <Grid item xs={6} sm={2.4}>
                    <Box textAlign="center" p={1.5} bgcolor="grey.50" borderRadius={1}>
                      <Typography variant="h6" color="warning.main" sx={{ fontSize: '1rem' }}>
                        {formatCurrency((detailTotals?.creditSales ?? selectedReport.creditSales))}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        Credit Sales
                      </Typography>
                    </Box>
                  </Grid>
                  {/* Total Sales and Orders */}
                  <Grid item xs={6}>
                    <Box textAlign="center" p={1.5} bgcolor="grey.100" borderRadius={1}>
                      <Typography variant="h6" color="success.main" sx={{ fontSize: '1rem' }}>
                        {formatCurrency((detailTotals?.totalSales ?? selectedReport.totalSales))}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        Total Sales
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box textAlign="center" p={1.5} bgcolor="grey.100" borderRadius={1}>
                      <Typography variant="h6" color="primary" sx={{ fontSize: '1rem' }}>
                        {(detailTotals?.totalOrders ?? selectedReport.totalOrders)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        Total Orders
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Card>

              {/* Product list - Compact */}
              <Card sx={{ p: 1.5 }}>
                <Typography variant="h6" gutterBottom sx={{ fontSize: '1.1rem' }}>Product Details</Typography>
                <TableContainer component={Paper} sx={{ maxHeight: 200 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Product</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>SKU</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Qty</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detailLoading ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ fontSize: '0.8rem' }}>Loading...</TableCell>
                        </TableRow>
                      ) : (detailProducts && detailProducts.length > 0) ? (
                        detailProducts.map((p, idx) => (
                          <TableRow key={idx}>
                            <TableCell sx={{ fontSize: '0.8rem' }}>{p.productName}</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>{p.productSku}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{p.quantitySold}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.8rem' }}>
                              {formatCurrency(getRoundedProductAmount(p.totalAmount))}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ fontSize: '0.8rem' }}>No product details</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {/* Total Amount for Products */}
                {detailProducts && detailProducts.length > 0 && (
                  <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1, textAlign: 'right' }}>
                    <Typography variant="body1" sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                      Total Products Amount: {formatCurrency(getRoundedProductsTotal())}
                    </Typography>
                  </Box>
                )}
              </Card>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDownloadPDF} 
            variant="contained" 
            color="primary"
            startIcon={<FileDownloadIcon />}
            disabled={!selectedReport}
          >
            Download PDF
          </Button>
          <Button onClick={handleCloseDetails} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default DayCloseReport;

